export const BIOTRON_MANUAL_DIAGNOSTICS_SCHEMA_VERSION = 1
export const BIOTRON_MANUAL_MIN_PROMPT_SIGNALS = 2

export const BIOTRON_MANUAL_PHASES = Object.freeze([
  {id: "prepare", seconds: 3, prompt: "Get ready. Do not touch Biotron until the next instruction."},
  {id: "baseline", seconds: 3, prompt: "Keep your hands away. Learning the normal signal…"},
  {id: "plant", seconds: 6, prompt: "Touch the plant or both leaf clips at least twice now."},
  {id: "plant_recovery", seconds: 2, prompt: "Stop touching the plant. Waiting for its notes to end…"},
  {id: "light", seconds: 6, prompt: "Cover and uncover the light sensor at least twice now."},
  {id: "light_recovery", seconds: 2, prompt: "Leave the light sensor uncovered. Waiting for it to settle…"}
])

export const BIOTRON_MANUAL_CHECKS = Object.freeze([
  {id: "plant_sensor", label: "Plant clips react when touched", kind: "assisted_probe"},
  {id: "light_sensor", label: "Light sensor reacts when covered", kind: "assisted_probe"},
  {id: "note_lifecycle", label: "Notes stop after each action ends", kind: "automatic_probe"},
  {id: "fingerprint", label: "Fingerprint pad changes the sound preset", kind: "human_attestation"},
  {id: "mute", label: "Mute pad stops the notes and turns them back on", kind: "human_attestation"},
  {id: "led", label: "Biotron lights respond", kind: "human_attestation"},
  {id: "audio", label: "Sound or incoming MIDI is visible in the music app", kind: "human_attestation"}
])

const freezeSession = session => Object.freeze({
  ...session,
  events: Object.freeze(session.events.map(event => Object.freeze({...event}))),
  evidence: Object.freeze(session.evidence.map(item => Object.freeze({...item})))
})

export function createBiotronManualSession({sessionId, deviceEpoch, plantChannel, lightChannel, lightMuted, now}) {
  if (!sessionId) throw new Error("Manual diagnostics require a session ID.")
  return freezeSession({
    schema_version: BIOTRON_MANUAL_DIAGNOSTICS_SCHEMA_VERSION,
    session_id: sessionId,
    device_epoch: Number(deviceEpoch) || 0,
    plant_channel: Number(plantChannel),
    light_channel: Number(lightChannel),
    light_muted: Boolean(lightMuted),
    started_at: now || new Date().toISOString(),
    valid: true,
    invalid_reason: "",
    events: [],
    evidence: []
  })
}

export function appendBiotronManualEvent(session, phase, event, now) {
  if (!session || !session.valid || !BIOTRON_MANUAL_PHASES.some(item => item.id === phase)) return session
  const type = ["note-on", "note-off", "pitch-bend", "control-change"].includes(event && event.type)
    ? event.type : "other"
  return freezeSession({
    ...session,
    events: [...session.events, {
      phase, type, channel: Number(event && event.channel) || 0,
      note: Number(event && event.note) || 0,
      value: Number(event && event.value) || 0,
      observed_at: now || new Date().toISOString()
    }]
  })
}

export function appendBiotronManualEvidence(session, {check, status, reason, kind, now}) {
  if (!session || !session.valid) return session
  const contract = BIOTRON_MANUAL_CHECKS.find(item => item.id === check)
  if (!contract || !["pass", "fail", "blocked"].includes(status) || kind !== contract.kind) {
    throw new Error("Invalid manual diagnostic evidence.")
  }
  return freezeSession({
    ...session,
    evidence: [...session.evidence, {
      check, status, reason: reason || "", kind,
      observed_at: now || new Date().toISOString()
    }]
  })
}

const phaseSignals = (session, phase, channel, types) => session.events.filter(event =>
  event.phase === phase && event.channel === channel && types.includes(event.type)
)

const activeNotes = events => {
  const active = new Map()
  for (const event of events) {
    if (event.type !== "note-on" && event.type !== "note-off") continue
    const key = `${event.channel}:${event.note}`
    const count = active.get(key) || 0
    if (event.type === "note-on") active.set(key, count + 1)
    else if (count > 0) active.set(key, count - 1)
  }
  return [...active.values()].reduce((sum, count) => sum + count, 0)
}

export function evaluateBiotronManualCapture(session) {
  if (!session || !session.valid) return session
  let next = session
  const sameChannel = session.plant_channel === session.light_channel
  const plantBaseline = phaseSignals(session, "baseline", session.plant_channel, ["note-on"]).length
  const plantSignals = phaseSignals(session, "plant", session.plant_channel, ["note-on"]).length
  const lightBaseline = phaseSignals(session, "baseline", session.light_channel,
    ["note-on", "pitch-bend"]).length
  const lightSignals = phaseSignals(session, "light", session.light_channel,
    ["note-on", "pitch-bend"]).length
  const plantAboveBaseline = plantBaseline === 0 ||
    plantSignals / 6 > (plantBaseline / 3) * 1.25
  const lightAboveBaseline = lightBaseline === 0 ||
    lightSignals / 6 > (lightBaseline / 3) * 1.25

  const plantStatus = sameChannel ? "blocked" : plantSignals === 0 ? "fail" :
    plantSignals < BIOTRON_MANUAL_MIN_PROMPT_SIGNALS ? "blocked" :
    !plantAboveBaseline ? "blocked" : "pass"
  const lightStatus = sameChannel ? "blocked" : lightSignals === 0 ?
    (session.light_muted ? "blocked" : "fail") :
    lightSignals < BIOTRON_MANUAL_MIN_PROMPT_SIGNALS ? "blocked" :
    !lightAboveBaseline ? "blocked" : "pass"

  next = appendBiotronManualEvidence(next, {
    check: "plant_sensor", status: plantStatus, kind: "assisted_probe",
    reason: sameChannel ? "shared_channel_cannot_attribute" : plantSignals === 0 ?
      "no_plant_note_on_during_prompt" : plantSignals < BIOTRON_MANUAL_MIN_PROMPT_SIGNALS ?
        "insufficient_repeatable_plant_signal" : plantStatus === "blocked" ?
        "background_activity_makes_causality_ambiguous" : "prompted_plant_signal_above_baseline"
  })
  next = appendBiotronManualEvidence(next, {
    check: "light_sensor", status: lightStatus, kind: "assisted_probe",
    reason: sameChannel ? "shared_channel_cannot_attribute" : lightSignals === 0 ?
      (session.light_muted ? "light_output_is_muted" : "no_light_signal_during_prompt") :
      lightSignals < BIOTRON_MANUAL_MIN_PROMPT_SIGNALS ? "insufficient_repeatable_light_signal" :
      lightStatus === "blocked" ? "background_activity_makes_causality_ambiguous" :
        "prompted_light_signal_above_baseline"
  })

  const plantLifecycleEvents = session.events.filter(event =>
    ["plant", "plant_recovery"].includes(event.phase)
  )
  const lightLifecycleEvents = session.events.filter(event =>
    ["light", "light_recovery"].includes(event.phase)
  )
  const lifecycleEvents = [...plantLifecycleEvents, ...lightLifecycleEvents]
  const noteOns = lifecycleEvents.filter(event => event.type === "note-on").length
  const stuck = activeNotes(plantLifecycleEvents) + activeNotes(lightLifecycleEvents)
  next = appendBiotronManualEvidence(next, {
    check: "note_lifecycle",
    status: noteOns === 0 ? "blocked" : stuck === 0 ? "pass" : "fail",
    kind: "automatic_probe",
    reason: noteOns === 0 ? "no_notes_to_evaluate" : stuck === 0 ?
      "all_prompted_notes_released" : `active_notes_after_recovery:${stuck}`
  })
  return next
}

export function invalidateBiotronManualSession(session, reason, now) {
  if (!session || !session.valid) return session
  return freezeSession({
    ...session, valid: false, invalid_reason: reason || "session_changed",
    invalidated_at: now || new Date().toISOString()
  })
}

export function biotronManualSummary(session) {
  if (!session || !session.valid) return "incomplete"
  const latest = new Map()
  for (const evidence of session.evidence) latest.set(evidence.check, evidence)
  if ([...latest.values()].some(item => item.status === "fail")) return "fail"
  if (BIOTRON_MANUAL_CHECKS.some(check =>
    !latest.has(check.id) || latest.get(check.id).status !== "pass"
  )) return "incomplete"
  return "pass"
}

export function createBiotronManualReport({session, firmwareVersion, browserEnvironment}) {
  const latest = new Map()
  if (session) for (const evidence of session.evidence) latest.set(evidence.check, evidence)
  const eventCounts = {}
  if (session) for (const event of session.events) {
    const key = `${event.phase}:${event.type}:ch${event.channel}`
    eventCounts[key] = (eventCounts[key] || 0) + 1
  }
  return {
    schema_version: BIOTRON_MANUAL_DIAGNOSTICS_SCHEMA_VERSION,
    created_at: new Date().toISOString(),
    product: "Biotron",
    summary: biotronManualSummary(session),
    session_id: session ? session.session_id : "unknown",
    firmware_version: firmwareVersion || "unknown",
    browser_environment: browserEnvironment || "unknown",
    invalid_reason: session ? session.invalid_reason : "missing_session",
    results: BIOTRON_MANUAL_CHECKS.map(check => {
      const evidence = latest.get(check.id)
      return {id: check.id, label: check.label, status: evidence ? evidence.status : "pending",
        evidence_kind: check.kind, reason: evidence ? evidence.reason : ""}
    }),
    aggregate_midi_counts: eventCounts
  }
}
